import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type VenueDto, type ClientSummaryDto } from '@/api/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MapPin, Plus, Pencil, Trash2, X, Building2 } from 'lucide-react'
import { AddressAutocomplete } from '@/components/shared/AddressAutocomplete'
import { formatAddress, addressLabels } from '@/lib/address'
import { isUs } from '@/lib/dates'
import { useAdminDates } from '@/hooks/useDates'

type VenueForm = {
  name: string
  addressLine1: string
  addressLine2: string
  city: string
  county: string
  postcode: string
  notes: string
  clientId: string
}

const emptyForm = (clientId = ''): VenueForm => ({
  name: '', addressLine1: '', addressLine2: '', city: '', county: '', postcode: '', notes: '', clientId,
})

function toPayload(f: VenueForm, defaultClientId?: string | null) {
  return {
    name: f.name.trim(),
    addressLine1: f.addressLine1.trim() || null,
    addressLine2: f.addressLine2.trim() || null,
    city: f.city.trim() || null,
    county: f.county.trim() || null,
    postcode: f.postcode.trim() || null,
    notes: f.notes.trim() || null,
    clientId: f.clientId || defaultClientId || null,
  }
}

// UK output is unchanged (line 1, city, postcode); US adds the state so it reads "City, ST 12345".
function venueAddressSummary(v: VenueDto, region?: string | null) {
  return formatAddress({ line1: v.addressLine1, city: v.city, county: isUs(region) ? v.county : null, postcode: v.postcode }, region) || null
}

export default function VenuesPage() {
  const { region } = useAdminDates()
  const queryClient = useQueryClient()

  const { data: venues = [], isLoading } = useQuery({
    queryKey: ['venues'],
    queryFn: () => adminApi.getVenues().then(r => r.data),
  })
  const { data: clients = [] } = useQuery<ClientSummaryDto[]>({
    queryKey: ['client-summaries'],
    queryFn: () => adminApi.getClientSummaries().then(r => r.data),
  })

  const [filterClientId, setFilterClientId] = useState<string>('')
  const [adding, setAdding] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<VenueForm>(emptyForm())

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['venues'] })

  const createMutation = useMutation({
    mutationFn: () => adminApi.createVenue(toPayload(form)),
    onSuccess: () => { invalidate(); setAdding(false); setForm(emptyForm()) },
  })

  const updateMutation = useMutation({
    mutationFn: (id: string) => adminApi.updateVenue(id, toPayload(form)),
    onSuccess: () => { invalidate(); setEditId(null); setForm(emptyForm()) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteVenue(id),
    onSuccess: invalidate,
  })

  function startAdd() {
    setAdding(true)
    setEditId(null)
    setForm(emptyForm(filterClientId))
  }

  function startEdit(v: VenueDto) {
    setEditId(v.id)
    setAdding(false)
    setForm({
      name: v.name,
      addressLine1: v.addressLine1 ?? '',
      addressLine2: v.addressLine2 ?? '',
      city: v.city ?? '',
      county: v.county ?? '',
      postcode: v.postcode ?? '',
      notes: v.notes ?? '',
      clientId: v.clientId ?? '',
    })
  }

  function cancel() { setEditId(null); setAdding(false); setForm(emptyForm()) }

  const filtered = filterClientId === '__global'
    ? venues.filter(v => !v.clientId)
    : filterClientId
      ? venues.filter(v => v.clientId === filterClientId)
      : venues

  const clientName = (id: string | null) =>
    id ? (clients.find(c => c.id === id)?.name ?? 'Unknown client') : null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Venues</h1>
          <p className="text-sm text-gray-500 mt-0.5">Training venue addresses used on bookings and pre-course packs.</p>
        </div>
        {!adding && !editId && (
          <Button size="sm" onClick={startAdd}>
            <Plus size={15} className="mr-1.5" /> Add venue
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-500 shrink-0">Show:</label>
        <select
          value={filterClientId}
          onChange={e => setFilterClientId(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
        >
          <option value="">All venues</option>
          <option value="__global">Global (no client)</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {filterClientId && (
          <button type="button" onClick={() => setFilterClientId('')}
            className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer">
            Clear
          </button>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin size={15} className="text-indigo-500" /> New venue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VenueFormFields form={form} onChange={setForm} clients={clients} />
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={() => createMutation.mutate()} disabled={!form.name.trim() || createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Save venue'}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancel}><X size={14} /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-sm text-gray-400 p-6">Loading…</p>
          ) : filtered.length === 0 && !adding ? (
            <div className="text-center py-16">
              <MapPin size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-500 font-medium">No venues{filterClientId ? ' matching this filter' : ' yet'}</p>
              {!filterClientId && <p className="text-sm text-gray-400 mt-1">Add a venue to attach it to bookings.</p>}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map(v => (
                editId === v.id ? (
                  <div key={v.id} className="p-4 bg-gray-50">
                    <VenueFormFields form={form} onChange={setForm} clients={clients} />
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" onClick={() => updateMutation.mutate(v.id)} disabled={!form.name.trim() || updateMutation.isPending}>
                        {updateMutation.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancel}><X size={14} /></Button>
                    </div>
                  </div>
                ) : (
                  <div key={v.id} className="flex items-start justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 text-sm">{v.name}</p>
                        {v.clientId && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-1.5 py-0.5">
                            <Building2 size={9} />{clientName(v.clientId)}
                          </span>
                        )}
                      </div>
                      {venueAddressSummary(v, region) && (
                        <p className="text-sm text-gray-500 mt-0.5 truncate">{venueAddressSummary(v, region)}</p>
                      )}
                      {v.notes && <p className="text-xs text-gray-400 mt-0.5 truncate">{v.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-3 shrink-0">
                      <button onClick={() => startEdit(v)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => { if (confirm(`Delete venue "${v.name}"?`)) deleteMutation.mutate(v.id) }}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export function VenueFormFields({
  form,
  onChange,
  clients,
  hideClientSelect = false,
}: {
  form: VenueForm
  onChange: (f: VenueForm) => void
  clients: ClientSummaryDto[]
  hideClientSelect?: boolean
}) {
  const { region } = useAdminDates()
  const labels = addressLabels(region)
  return (
    <div className="space-y-3">
      <div className={`grid gap-3 ${hideClientSelect ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <div className="space-y-1.5">
          <Label>Name *</Label>
          <Input value={form.name} onChange={e => onChange({ ...form, name: e.target.value })} placeholder="e.g. Manchester Training Suite" autoFocus />
        </div>
        {!hideClientSelect && (
          <div className="space-y-1.5">
            <Label>Client</Label>
            <select
              value={form.clientId}
              onChange={e => onChange({ ...form, clientId: e.target.value })}
              className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Global (no client)</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Address lookup</Label>
        <AddressAutocomplete
          region={region}
          onSelect={r => onChange({ ...form, addressLine1: r.addressLine1, city: r.city, county: r.county, postcode: r.postcode })}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Address line 1</Label>
          <Input value={form.addressLine1} onChange={e => onChange({ ...form, addressLine1: e.target.value })} placeholder="Street address" />
        </div>
        <div className="space-y-1.5">
          <Label>Address line 2</Label>
          <Input value={form.addressLine2} onChange={e => onChange({ ...form, addressLine2: e.target.value })} placeholder="Suite, unit, floor…" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label>Town / City</Label>
          <Input value={form.city} onChange={e => onChange({ ...form, city: e.target.value })} placeholder="Manchester" />
        </div>
        <div className="space-y-1.5">
          <Label>{labels.county}</Label>
          <Input value={form.county} onChange={e => onChange({ ...form, county: e.target.value })} placeholder={labels.countyPlaceholder} />
        </div>
        <div className="space-y-1.5">
          <Label>{labels.postcode}</Label>
          <Input value={form.postcode} onChange={e => onChange({ ...form, postcode: e.target.value })} placeholder={labels.postcodePlaceholder} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Notes</Label>
        <Input value={form.notes} onChange={e => onChange({ ...form, notes: e.target.value })} placeholder="Parking, door codes, directions…" />
      </div>
    </div>
  )
}
